import { Close } from "@mui/icons-material";
import { Box, Button, CircularProgress, IconButton, Modal, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { Color } from "../../../../GlobalStyles";
import CCService from "../../../../services/httpServiceWithoutSuperAdmin";

function RecruiterRequestCard({ request }) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(null);
  const service = new CCService();
  const queryClient = useQueryClient();

  const handleOpenModal = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleApprove = async () => {
    setLoading("approve");
    try {
      const response = await service.put(`/cee/recruiter/update-recruiter-status`, {
        user_id: request.user_id,
        email_id: request.email_id,
        status: "approved"
      });

      if (response.status === 200) {
        queryClient.setQueriesData(["recruiter-requests"], (oldData) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.map((req) => 
                req.email_id === request.email_id ? {...req, status: "approved"} : req
              )
            }))
          };
        });
      }

      setShowModal(false);
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
        setLoading(null);
    }
  };

  const handleReject = async () => {
    setLoading("reject");
    try {
      const response = await service.put(`/cee/recruiter/update-recruiter-status`, {
        user_id: request.user_id,
        email_id: request.email_id,
        status: "rejected"
      });

      if (response.status === 200) {
        queryClient.setQueriesData(["recruiter-requests"], (oldData) => {
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.map((req) => 
                req.email_id === request.email_id ? {...req, status: "rejected"} : req
              )
            }))
          };
        });
      }

      setShowModal(false);
    } catch (err) {
      console.error("Unexpected error:", err);
    } finally {
      setLoading(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      case "pending":
        return "#ff9800";
      default:
        return "#757575";
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        p: 2,
        m: 2,
        borderRadius: 2,
        backgroundColor: "#fff",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          boxShadow: 1,
          transform: "translateY(-3px)",
        },
      }}
      onClick={handleOpenModal}
      style={{ cursor: "pointer" }}
    >
      <Typography sx={{ flex: 1, fontWeight: 500 }}>{request.data?.first_name || "N/A"}</Typography>
      <Typography sx={{ flex: 1 }}>{request.data?.company_name || "N/A"}</Typography>
      <Typography sx={{ flex: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{request.email_id}</Typography>
      <Typography sx={{ 
        flex: 1, 
        textTransform: "capitalize",
        color: getStatusColor(request.status),
        fontWeight: 500
      }}>
        {request.status}
      </Typography>
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
      <Button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenModal();
          }}
          sx={{
            backgroundColor: Color.primary3,
            color: "#fff",
            px: 3,
            py: 1,
            borderRadius: 2,
            transition: "background-color 0.3s ease",
            "&:hover": {
              backgroundColor: "#005f99",
            },
          }}
        >
          View
        </Button>
      </Box>

      <CustomModal 
        showModal={showModal} 
        handleCloseModal={handleCloseModal} 
        request={request} 
        handleApprove={handleApprove} 
        handleReject={handleReject} 
        loading={loading}
      />
    </Box>
  );
}

export default RecruiterRequestCard;

const CustomModal = ({ showModal, handleCloseModal, request, handleApprove, handleReject, loading }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      case "pending":
        return "#ff9800";
      default:
        return "#757575";
    }
  };

  const renderActionButtons = () => {
    switch (request.status) {
      case "pending":
        return (
          <>
            <Button variant="contained" color="primary" onClick={handleApprove} disabled={loading === "approve"}>
              {loading === "approve" ? <CircularProgress size={20} /> : "Approve"}
            </Button>
            <Button variant="outlined" color="error" onClick={handleReject} disabled={loading === "reject"}>
              {loading === "reject" ? <CircularProgress size={20} /> : "Reject"}
            </Button>
          </>
        );
      // case "approved":
      //   return (
      //     <Button variant="outlined" color="error" onClick={handleReject} disabled={loading === "reject"}>
      //       {loading === "reject" ? <CircularProgress size={20} /> : "Reject"}
      //     </Button>
      //   );
      // case "rejected":
      //   return (
      //     <Button variant="contained" color="primary" onClick={handleApprove} disabled={loading === "approve"}>
      //       {loading === "approve" ? <CircularProgress size={20} /> : "Approve"}
      //     </Button>
      //   );
      default:
        return null;
    }
  };

  return (
    <Modal open={showModal} onClose={handleCloseModal}>
      <Box
        component={motion.div}
        initial={{ x: "100%", opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: "100%", opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          backgroundColor: "#fff",
          height: "100vh",
          width: { xs: "100%", sm: "90%", md: "600px" },
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          overflowY: "auto",
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        {/* Modal Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "24px", flex: 1 }}>
            Recruiter Request Details
          </Typography>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            sx={{ color: "gray" }}
          >
            <Close />
          </IconButton>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", gap: 1, my: 2 }}>
          {renderActionButtons()}
        </Box>

        {/* Request Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Email:</strong> {request.email_id}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, color: getStatusColor(request.status), fontWeight: 500 }}>
            <strong>Status:</strong> {request.status.toUpperCase()}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>First Name:</strong> {request.data?.first_name || "N/A"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Company Name:</strong> {request.data?.company_name || "N/A"}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Phone Number:</strong> {request.data?.ph_no || "N/A"}
          </Typography>
          {request.data?.website && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Website:</strong>{" "}
              <a 
                href={request.data.website} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: "#1976d2", textDecoration: "none" }}
              >
                {request.data.website}
              </a>
            </Typography>
          )}
          {request.created_at && (
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Created At:</strong> {new Date(request.created_at).toLocaleString()}
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
